import {apiRequest} from './client';

export type ThemeBaseId = 'source' | 'casper' | 'edition';
export type ThemeJobStatus = 'queued' | 'generating' | 'validating' | 'ready' | 'failed' | 'cancelled';
export type ThemeReferenceIntent = 'color' | 'typography' | 'layout' | 'overall';
export type ThemeSetupInput = {
    version: 1;
    references: Array<{kind: 'url'; url: string; intent: ThemeReferenceIntent} | {kind: 'image'; fileIndex: number; intent: ThemeReferenceIntent}>;
    avoid: string;
    fonts: {heading: string | null; body: string | null};
    preset: 'editorial' | 'minimal' | 'bold' | 'warm' | 'technical' | null;
    palette: Partial<ThemeRecipe['tokens']['colors']> | null;
    startingPoint: ThemeBaseId;
};
export type ThemeSetupPreparation = {
    setup: ThemeSetupInput;
    palette: ThemeRecipe['tokens']['colors'];
    dropped: Array<{index: number; source: string; reason: string; code: string}>;
    accepted: number;
};

export interface ThemeRecipe {
    schemaVersion: '1';
    base: {id: ThemeBaseId; version: string};
    tokens: {
        colors: {accent: string; background: string; surface: string; text: string; muted: string};
        typography: {heading: string; body: string; scale: 'compact' | 'balanced' | 'expressive'};
        spacing: {density: 'compact' | 'comfortable' | 'airy'; radius: number};
        widths: {content: number; wide: number};
    };
    sections: Array<{id: string; visible: boolean}>;
    assets: {logo: string | null; cover: string | null};
}

export interface ThemeJob {
    id: string;
    status: ThemeJobStatus;
    kind: 'build' | 'refinement';
    error_code: string | null;
    error_message: string | null;
}

export interface ThemeVersion {
    id: string;
    ordinal: number;
    recipe: ThemeRecipe;
    artifact_hash: string;
    created_at: string;
    published_at: string | null;
}

export interface ThemeProject {
    id: string;
    name: string;
    tenant_id: string | null;
    publication_brief: string;
    setup?: ThemeSetupInput | null;
    draft_recipe: ThemeRecipe;
    draft_revision: number;
    conversation: Array<{role: 'user' | 'assistant'; content: string; at: string}>;
    published_version_id: string | null;
    jobs: ThemeJob[];
    versions: ThemeVersion[];
    usage: {builds_used: number; input_tokens: number; output_tokens: number; cost_micros: number};
}

export interface ThemeCapabilities {
    generation_enabled: boolean;
    direct_controls_enabled: boolean;
    saved_versions_enabled: boolean;
    publish_enabled: boolean;
    allowance: number;
    reasons: string[];
    bases: Array<{id: ThemeBaseId; name: string; version: string; license: string}>;
}

export const getThemeCapabilities = () => apiRequest<ThemeCapabilities>('/theme-builder/capabilities');
export interface ThemeProjectSummary {
    id: string;
    name: string;
    publication_brief: string;
    updated_at: string;
    published_version_id: string | null;
    colors: ThemeRecipe['tokens']['colors'] | null;
}

export const getThemeProjects = () => apiRequest<ThemeProjectSummary[]>('/theme-builder/projects');
export const renameThemeProject = (id: string, name: string) => apiRequest<ThemeProject>(`/theme-builder/projects/${id}`, {method: 'PATCH', body: JSON.stringify({name})});
export const getThemeProject = (id: string) => apiRequest<ThemeProject>(`/theme-builder/projects/${id}`);
export const getThemeDraftPreviewUrl = (id: string, recipe: ThemeRecipe, revision: number) => `/api/theme-builder/projects/${encodeURIComponent(id)}/preview?${new URLSearchParams({recipe: JSON.stringify({tokens: recipe.tokens, sections: recipe.sections}), revision: String(revision)})}`;

function setupFormData(data: {setup?: ThemeSetupInput; referenceImages?: File[]; logo?: File | null}) {
    const body = new FormData();
    if (data.setup) {
        body.append('setup', JSON.stringify(data.setup));
    }
    for (const image of data.referenceImages || []) {
        body.append('referenceImages', image);
    }
    if (data.logo) {
        body.append('logo', data.logo);
    }
    return body;
}

export function prepareThemeSetup(data: {setup: ThemeSetupInput; referenceImages: File[]; logo?: File | null}) {
    return apiRequest<ThemeSetupPreparation>('/theme-builder/setup/prepare', {method: 'POST', body: setupFormData(data)});
}

export function createThemeProject(data: {name: string; brief: string; baseId: ThemeBaseId; referenceUrl?: string; logo?: File | null; setup?: ThemeSetupInput; referenceImages?: File[]}) {
    const body = setupFormData(data);
    body.append('name', data.name);
    body.append('brief', data.brief);
    body.append('baseId', data.baseId);
    if (data.referenceUrl) {
        body.append('referenceUrl', data.referenceUrl);
    }
    return apiRequest<ThemeProject>('/theme-builder/projects', {method: 'POST', body});
}

function idempotencyKey() {
    return globalThis.crypto.randomUUID();
}

export const generateTheme = (projectId: string) => apiRequest<ThemeJob>(`/theme-builder/projects/${projectId}/generate`, {method: 'POST', headers: {'Idempotency-Key': idempotencyKey()}});
export const refineTheme = (projectId: string, message: string) => apiRequest<ThemeJob>(`/theme-builder/projects/${projectId}/refinements`, {method: 'POST', headers: {'Idempotency-Key': idempotencyKey()}, body: JSON.stringify({message})});
export const updateThemeDraft = (projectId: string, revision: number, recipe: ThemeRecipe) => apiRequest<ThemeProject>(`/theme-builder/projects/${projectId}/draft`, {method: 'PATCH', body: JSON.stringify({revision, recipe})});
export const saveThemeVersion = (projectId: string) => apiRequest<ThemeVersion>(`/theme-builder/projects/${projectId}/versions`, {method: 'POST'});
export const createThemePreview = (versionId: string) => apiRequest<{url: string; expires_at: string}>(`/theme-builder/versions/${versionId}/preview-grants`, {method: 'POST'});
export const publishThemeVersion = (versionId: string) => apiRequest<{version_id: string; previous_version_id: string | null}>(`/theme-builder/versions/${versionId}/publish`, {method: 'POST'});
export const cancelThemeJob = (jobId: string) => apiRequest<ThemeJob>(`/theme-builder/jobs/${jobId}/cancel`, {method: 'POST'});
export const getDfyOrders = () => apiRequest<Array<{id: string; status: string; amount_cents: number}>>('/theme-builder/dfy/orders');
export const startDfyCheckout = (tenantId: string) => apiRequest<{mode: string; checkout_id: string}>('/theme-builder/dfy/checkout', {method: 'POST', body: JSON.stringify({tenantId})});
export interface DfyAdminOrder {id: string; account_id: string; tenant_id: string | null; project_id: string; theme_slug: string; status: string; amount_cents: number; assigned_to_account_id: string | null; delivered_version_id: string | null}
export const getAdminDfyOrders = () => apiRequest<DfyAdminOrder[]>('/theme-builder/dfy/admin/orders');
export const updateAdminDfyOrder = (id: string, status: string, note?: string) => apiRequest<DfyAdminOrder>(`/theme-builder/dfy/admin/orders/${id}`, {method: 'PATCH', body: JSON.stringify({status, note})});
export function uploadDfyTheme(id: string, theme: File) {
    const body = new FormData();
    body.append('theme', theme);
    return apiRequest<{order: DfyAdminOrder; version: ThemeVersion}>(`/theme-builder/dfy/admin/orders/${id}/artifact`, {method: 'POST', body});
}
